/**
 * Chatbots API Service
 * 
 * Handles all chatbot-related API calls with comprehensive logging
 * and error handling for the CleverAct AI backend integration.
 */

import { api } from './api'
import type { Chatbot, CreateChatbotRequest, ChatbotAnalytics, PaginatedResponse } from '../types'
import { API_ENDPOINTS } from '../utils/constants'
import { logger, loggerUtils } from '../utils/logger'

/**
 * Chatbots Service Class
 *
 * Provides methods for chatbot CRUD operations and analytics
 */
class ChatbotsService {
  /**
   * Fetch all chatbots for the authenticated user
   * @param page - Page number for pagination
   * @param limit - Number of items per page
   * @returns Promise<PaginatedResponse<Chatbot>>
   */
  async getChatbots(page: number = 1, limit: number = 20): Promise<PaginatedResponse<Chatbot>> {
    loggerUtils.functionEntry('ChatbotsService.getChatbots', { page, limit })

    try {
      logger.info('Fetching user chatbots', {
        component: 'ChatbotsService',
        action: 'fetch_chatbots',
        data: { page, limit }
      })

      const response = await api.get<PaginatedResponse<Chatbot>>(API_ENDPOINTS.CHATBOTS, {
        params: { page, limit }
      })

      // Handle different possible response formats from CleverAct AI backend
      let normalizedResponse: PaginatedResponse<Chatbot>
      
      if (Array.isArray(response.data)) {
        // Direct array response
        normalizedResponse = {
          items: response.data,
          total: response.data.length,
          page: page,
          size: limit,
          totalPages: Math.ceil(response.data.length / limit)
        }
      } else if (response.data.items && Array.isArray(response.data.items)) {
        // Paginated response
        normalizedResponse = response.data
      } else if (response.data.chatbots && Array.isArray(response.data.chatbots)) {
        // Alternative format with chatbots key
        normalizedResponse = {
          items: response.data.chatbots,
          total: response.data.total || response.data.chatbots.length,
          page: response.data.page || page,
          size: response.data.size || limit,
          totalPages: response.data.totalPages || Math.ceil((response.data.total || response.data.chatbots.length) / limit)
        }
      } else {
        // Fallback - assume empty result
        normalizedResponse = {
          items: [],
          total: 0,
          page: page,
          size: limit,
          totalPages: 0
        }
      }

      logger.info('Successfully fetched chatbots', {
        component: 'ChatbotsService',
        action: 'fetch_success',
        data: { 
          total: normalizedResponse.total,
          page: normalizedResponse.page,
          count: normalizedResponse.items.length,
          rawResponse: response.data
        }
      })
      
      loggerUtils.functionExit('ChatbotsService.getChatbots', normalizedResponse)
      return normalizedResponse
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch chatbots'
      
      logger.error('Failed to fetch chatbots', {
        component: 'ChatbotsService',
        error: errorMessage,
        action: 'fetch_error',
        data: { page, limit }
      })
      
      throw error
    }
  }

  /**
   * Fetch a single chatbot by ID
   * @param id - Chatbot ID
   * @returns Promise<Chatbot>
   */
  async getChatbot(id: string): Promise<Chatbot> {
    loggerUtils.functionEntry('ChatbotsService.getChatbot', { id })
    
    try {
      logger.info('Fetching chatbot details', {
        component: 'ChatbotsService',
        action: 'fetch_chatbot',
        data: { id }
      })
      
      const response = await api.get<Chatbot>(API_ENDPOINTS.CHATBOT_BY_ID(id))
      
      logger.info('Successfully fetched chatbot', {
        component: 'ChatbotsService',
        action: 'fetch_chatbot_success',
        data: { 
          id, 
          name: response.data.name,
          hasAllFields: !!(response.data.tone && response.data.personality && response.data.industry)
        }
      })
      
      loggerUtils.functionExit('ChatbotsService.getChatbot', response.data)
      return response.data
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch chatbot'
      
      logger.error('Failed to fetch chatbot', {
        component: 'ChatbotsService',
        error: errorMessage,
        action: 'fetch_chatbot_error',
        data: { id }
      })
      
      throw error
    }
  }

  /**
   * Create a new chatbot
   * @param data - Chatbot creation data
   * @returns Promise<Chatbot>
   */
  async createChatbot(data: CreateChatbotRequest): Promise<Chatbot> {
    loggerUtils.functionEntry('ChatbotsService.createChatbot', { name: data.name })
    
    try {
      logger.info('Creating new chatbot', {
        component: 'ChatbotsService',
        action: 'create_chatbot',
        data: { name: data.name, industry: data.industry }
      })
      
      const response = await api.post<Chatbot>(API_ENDPOINTS.CHATBOTS, data)
      
      logger.info('Successfully created chatbot', {
        component: 'ChatbotsService',
        action: 'create_success',
        data: { id: response.data.id, name: response.data.name }
      })
      
      loggerUtils.userAction('chatbot_created', 'chatbot_form', { 
        chatbotId: response.data.id,
        name: response.data.name 
      })
      
      loggerUtils.functionExit('ChatbotsService.createChatbot', response.data)
      return response.data
      
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create chatbot'
      
      logger.error('Failed to create chatbot', {
        component: 'ChatbotsService',
        error: errorMessage,
        action: 'create_error',
        data: { name: data.name }
      })
      
      loggerUtils.userAction('chatbot_creation_failed', 'chatbot_form', { 
        error: errorMessage,
        name: data.name 
      })
      
      const detail = (error as any)?.data
      if (detail?.code === 'limit_reached' && detail?.metric === 'chatbots') {
        const limit = detail?.limit ?? ''
        throw new Error(`Chatbots limit reached (${limit}). Upgrade your plan to create more chatbots.`)
      }
      throw new Error((error as any)?.message || 'Failed to create chatbot')
    }
  }

  /**
   * Update an existing chatbot
   * @param id - Chatbot ID
   * @param data - Updated chatbot data
   * @returns Promise<Chatbot>
   */
  async updateChatbot(id: string, data: Partial<CreateChatbotRequest>): Promise<Chatbot> {
    loggerUtils.functionEntry('ChatbotsService.updateChatbot', { id, updates: Object.keys(data) })
    
    try {
      logger.info('Updating chatbot', {
        component: 'ChatbotsService',
        action: 'update_chatbot',
        data: { id, updates: Object.keys(data) }
      })
      
      const response = await api.put<Chatbot>(API_ENDPOINTS.CHATBOT_BY_ID(id), data)
      
      logger.info('Successfully updated chatbot', {
        component: 'ChatbotsService',
        action: 'update_success',
        data: { id, name: response.data.name }
      })
      
      loggerUtils.userAction('chatbot_updated', 'chatbot_form', { 
        chatbotId: id,
        updates: Object.keys(data)
      })
      
      loggerUtils.functionExit('ChatbotsService.updateChatbot', response.data)
      return response.data
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update chatbot'
      
      logger.error('Failed to update chatbot', {
        component: 'ChatbotsService',
        error: errorMessage,
        action: 'update_error',
        data: { id }
      })
      
      throw error
    }
  }

  /**
   * Delete a chatbot
   * @param id - Chatbot ID
   * @returns Promise<void>
   */
  async deleteChatbot(id: string): Promise<void> {
    loggerUtils.functionEntry('ChatbotsService.deleteChatbot', { id })
    
    try {
      logger.warn('Deleting chatbot', {
        component: 'ChatbotsService',
        action: 'delete_chatbot',
        data: { id }
      })
      
      await api.delete(API_ENDPOINTS.CHATBOT_BY_ID(id))
      
      logger.info('Successfully deleted chatbot', {
        component: 'ChatbotsService',
        action: 'delete_success',
        data: { id }
      })
      
      loggerUtils.userAction('chatbot_deleted', 'chatbot_actions', { chatbotId: id })
      
      loggerUtils.functionExit('ChatbotsService.deleteChatbot')
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete chatbot'
      
      logger.error('Failed to delete chatbot', {
        component: 'ChatbotsService',
        error: errorMessage,
        action: 'delete_error',
        data: { id }
      })
      
      throw error
    }
  }

  /**
   * Fetch chatbot analytics
   * @param id - Chatbot ID
   * @returns Promise<ChatbotAnalytics>
   */
  async getChatbotAnalytics(id: string): Promise<ChatbotAnalytics> {
    loggerUtils.functionEntry('ChatbotsService.getChatbotAnalytics', { id })
    
    try {
      logger.info('Fetching chatbot analytics', {
        component: 'ChatbotsService',
        action: 'fetch_analytics',
        data: { id }
      })
      
      // Use the composite agent-stats endpoint (per-bot analytics)
      const response = await api.get(`/analytics/agent-stats`, { params: { chatbot_id: id, days: 30 } })
      const stats = response.data

      // Map AgentStatsResponse → ChatbotAnalytics
      const mapped: ChatbotAnalytics = {
        total_conversations: stats.overview?.total_conversations ?? 0,
        total_messages: stats.overview?.total_messages ?? 0,
        avg_response_time: stats.overview?.avg_response_time_ms ? stats.overview.avg_response_time_ms / 1000 : 0,
        user_satisfaction: stats.overview?.satisfaction_rate ?? 0,
        top_questions: [],
        conversation_trends: [],
      }

      logger.info('Successfully fetched analytics', {
        component: 'ChatbotsService',
        action: 'analytics_success',
        data: {
          id,
          totalConversations: mapped.total_conversations,
          totalMessages: mapped.total_messages
        }
      })

      loggerUtils.functionExit('ChatbotsService.getChatbotAnalytics', mapped)
      return mapped
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch analytics'
      
      logger.error('Failed to fetch chatbot analytics', {
        component: 'ChatbotsService',
        error: errorMessage,
        action: 'analytics_error',
        data: { id }
      })
      
      throw error
    }
  }

  /**
   * Get dashboard summary statistics from the backend analytics endpoint
   * @returns Promise with summary stats
   */
  async getDashboardStats(): Promise<{
    totalChatbots: number
    totalMessages: number
    avgResponseTime: number
    userSatisfaction: number
  }> {
    loggerUtils.functionEntry('ChatbotsService.getDashboardStats')
    
    try {
      logger.info('Fetching dashboard statistics from analytics endpoint', {
        component: 'ChatbotsService',
        action: 'fetch_dashboard_stats'
      })
      
      // Use the real backend analytics endpoint
      const response = await api.get(API_ENDPOINTS.ANALYTICS)
      
      logger.info('Analytics API response received', {
        component: 'ChatbotsService',
        action: 'analytics_response',
        data: { 
          hasOverview: !!response.data.overview,
          hasChatbotAnalytics: !!response.data.chatbot_analytics,
          rawResponse: response.data
        }
      })
      
      // Extract overview data from the backend response
      const overview = response.data.overview || {}
      
      // Calculate average response time from performance metrics or use default
      let avgResponseTime = 0.8 // Default fallback
      if (response.data.performance_metrics?.avg_response_time) {
        avgResponseTime = response.data.performance_metrics.avg_response_time
      }
      
      // Calculate user satisfaction from performance metrics or use default
      let userSatisfaction = 94 // Default fallback
      if (response.data.performance_metrics?.user_satisfaction) {
        userSatisfaction = response.data.performance_metrics.user_satisfaction
      } else if (response.data.performance_metrics?.engagement_score) {
        userSatisfaction = Math.round(response.data.performance_metrics.engagement_score)
      }
      
      const stats = {
        totalChatbots: overview.total_chatbots || 0,
        totalMessages: overview.total_messages || 0,
        avgResponseTime: avgResponseTime,
        userSatisfaction: userSatisfaction
      }
      
      logger.info('Successfully processed dashboard stats', {
        component: 'ChatbotsService',
        action: 'dashboard_stats_success',
        data: stats
      })
      
      loggerUtils.functionExit('ChatbotsService.getDashboardStats', stats)
      return stats
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch dashboard stats'
      
      logger.error('Failed to fetch dashboard statistics', {
        component: 'ChatbotsService',
        error: errorMessage,
        action: 'dashboard_stats_error'
      })
      
      // Return fallback stats
      const fallbackStats = {
        totalChatbots: 0,
        totalMessages: 0,
        avgResponseTime: 0,
        userSatisfaction: 0
      }
      
      loggerUtils.functionExit('ChatbotsService.getDashboardStats', fallbackStats)
      return fallbackStats
    }
  }
}

// Export singleton instance
export const chatbotsService = new ChatbotsService()

// Export class for testing
export { ChatbotsService }
